import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { AccountShell } from "@/components/customer/AccountShell";
import { OrderStatusBadge, PaymentStatusBadge } from "@/components/customer/OrderStatusBadge";
import { orderErrorMessage } from "@/components/customer/OrdersMessages";
import { Button } from "@/components/ui/button";
import { HelpCircle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { CUSTOMER_ORDERS_ENABLED } from "@/lib/customer/feature-visibility";
import { useCustomerOrderDetail } from "@/hooks/useCustomerOrders";
import { formatISTDate, formatISTDateTime } from "@/lib/date/ist";
import { OrderProgress } from "@/components/customer/OrderProgress";
import { RefreshCcw } from "lucide-react";
import { useEffect, useState } from "react";
import { formatPaise } from "@/lib/format/money";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_customer/account/orders/$orderId")({
  beforeLoad: () => {
    if (!CUSTOMER_ORDERS_ENABLED) throw redirect({ to: "/account", replace: true });
  },
  component: OrderDetailPage,
  head: () => ({
    meta: [
      { title: "Order details | Poonam's Healthy Kitchen" },
      {
        name: "description",
        content: "Items, totals and delivery details for your Poonam's Healthy Kitchen order.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function OrderDetailPage() {
  const { orderId } = Route.useParams();
  const { data, isLoading, isError, error, refetch, dataUpdatedAt } = useCustomerOrderDetail(orderId);
  const [lastUpdated, setLastUpdated] = useState<string>("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (dataUpdatedAt) {
      setLastUpdated(new Date(dataUpdatedAt).toLocaleTimeString());
    }
  }, [dataUpdatedAt]);

  const handleManualRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  };

  if (isLoading) {
    return (
      <AccountShell title="Order details">
        <div className="space-y-3" aria-busy="true" aria-live="polite">
          <span className="sr-only">Loading this order</span>
          <Skeleton className="h-20 w-full rounded-lg" />
          <Skeleton className="h-48 w-full rounded-lg" />
        </div>
      </AccountShell>
    );
  }

  if (isError || !data) {
    return (
      <AccountShell title="Order details">
        <div className="rounded-lg border border-border/60 bg-card p-8 text-center">
          <p role="alert" className="text-sm text-muted-foreground">
            {orderErrorMessage(error)}
          </p>
          <Button asChild variant="outline" className="mt-5">
            <Link to="/account/orders">Back to your orders</Link>
          </Button>
        </div>
      </AccountShell>
    );
  }

  const { order, delivery, totals, payment, lines } = data;
  const itemCount = totals.line_count ?? lines.length;

  return (
    <AccountShell
      title={order.order_ref ?? "Order details"}
      description={`Placed ${formatISTDateTime(order.created_at)}`}
    >
      <div className="mb-6 rounded-xl border border-border/60 bg-card p-6 shadow-sm">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Order Progress</h3>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleManualRefresh}
              disabled={isRefreshing || isLoading}
              className="h-8 text-xs gap-2"
            >
              <RefreshCcw className={cn("h-3 w-3", isRefreshing && "animate-spin")} />
              {isRefreshing ? "Refreshing..." : "Refresh Status"}
            </Button>
            {lastUpdated && (
              <div className="flex items-center text-[10px] text-muted-foreground">
                Last updated: {lastUpdated}
              </div>
            )}
          </div>
        </div>
        <OrderProgress status={order.status} />
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-6">
        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground leading-none">Order Status</span>
          <OrderStatusBadge status={order.status} />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground leading-none">Payment</span>
          <PaymentStatusBadge status={payment.payment_status} />
        </div>
        <Button asChild variant="ghost" size="sm" className="ml-auto">
          <Link to="/account/orders">Back to your orders</Link>
        </Button>
        <Button asChild variant="outline" size="sm" className="bg-primary/5 border-primary/20">
          <Link to="/contact">
            <HelpCircle className="mr-2 h-4 w-4" />
            Need Help?
          </Link>
        </Button>
      </div>

      <section
        aria-labelledby="order-items-heading"
        className="rounded-lg border border-border/60 bg-card"
      >
        <header className="flex items-baseline justify-between border-b border-border/60 px-4 py-3">
          <h2 id="order-items-heading" className="text-sm font-semibold">
            Items
          </h2>
          <p className="text-xs text-muted-foreground">
            {itemCount} {itemCount === 1 ? "item" : "items"}
          </p>
        </header>
        <ul className="divide-y divide-border/60">
          {lines.map((line, i) => (
            <li key={`${line.line_no ?? i}`} className="flex flex-wrap gap-2 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{line.title ?? "Item"}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {line.quantity ?? 0} × {formatPaise(line.unit_price_paise)}
                  {line.meal_slot ? ` · ${line.meal_slot}` : ""}
                  {line.delivery_date ? ` · ${formatISTDate(line.delivery_date)}` : ""}
                </p>
                {line.plan ? (
                  <dl className="mt-2 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-0.5 text-xs">
                    {(
                      [
                        ["Start date", line.plan.start_date ? formatISTDate(line.plan.start_date) : null],
                        ["Duration", line.plan.duration],
                        ["Meals", line.plan.meals],
                        ["Delivery days", line.plan.delivery_days],
                        ["Preference", line.plan.preference],
                        [
                          "Your dish choices",
                          line.plan.selected_dishes.length > 0
                            ? line.plan.selected_dishes.map((d) => d.name).join(", ")
                            : "Not recorded for this order",
                        ],
                      ] as const
                    ).map(([k, v]) => (
                      <div key={k} className="contents">
                        <dt className="text-muted-foreground">{k}</dt>
                        <dd className="font-medium">{v || "—"}</dd>
                      </div>
                    ))}
                  </dl>
                ) : null}
                {line.plan?.dish_selections && line.plan.dish_selections.length > 0 ? (
                  <div className="mt-2">
                    <p className="text-xs text-muted-foreground">Dishes for each delivery</p>
                    <ul className="mt-1 space-y-0.5 text-xs">
                      {line.plan.dish_selections.map((d, i) => (
                        <li key={`${d.date}-${d.slot}-${i}`} className="flex flex-wrap gap-x-2">
                          <span className="text-muted-foreground">
                            {formatISTDate(d.date)} · <span className="capitalize">{d.slot}</span>
                          </span>
                          <span className="font-medium">{d.dish_name || "—"}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
              <p className="text-sm font-semibold">{formatPaise(line.line_total_paise)}</p>
            </li>
          ))}
          {lines.length === 0 ? (
            <li className="px-4 py-3 text-sm text-muted-foreground">
              No itemised lines are available for this order.
            </li>
          ) : null}
        </ul>
        <div className="flex items-baseline justify-between border-t border-border/60 px-4 py-3">
          <p className="text-sm font-semibold">Order total</p>
          <p className="text-base font-semibold">{formatPaise(totals.total_amount_paise)}</p>
        </div>
      </section>

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <section
          aria-labelledby="order-delivery-heading"
          className="rounded-lg border border-border/60 bg-card p-4"
        >
          <h2 id="order-delivery-heading" className="text-sm font-semibold">
            Delivery
          </h2>
          <address className="mt-2 space-y-1 text-sm not-italic text-muted-foreground">
            {delivery.address_line_1 ? <p>{delivery.address_line_1}</p> : null}
            {delivery.address_line_2 ? <p>{delivery.address_line_2}</p> : null}
            <p>
              {[delivery.neighbourhood, delivery.pincode].filter(Boolean).join(" · ") || "—"}
            </p>
          </address>
          <dl className="mt-3 space-y-1 text-xs text-muted-foreground">
            <div className="flex gap-2">
              <dt className="font-medium text-foreground/80">Preferred date</dt>
              <dd>{formatISTDate(delivery.preferred_delivery_date)}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="font-medium text-foreground/80">Preferred time</dt>
              <dd>{delivery.preferred_delivery_time ?? "—"}</dd>
            </div>
            {delivery.special_instructions ? (
              <div className="flex gap-2">
                <dt className="font-medium text-foreground/80">Notes</dt>
                <dd>{delivery.special_instructions}</dd>
              </div>
            ) : null}
          </dl>
        </section>

        <section
          aria-labelledby="order-payment-heading"
          className="rounded-lg border border-border/60 bg-card p-4"
        >
          <h2 id="order-payment-heading" className="text-sm font-semibold">
            Payment
          </h2>
          <dl className="mt-2 space-y-1 text-xs text-muted-foreground">
            <div className="flex gap-2">
              <dt className="font-medium text-foreground/80">Status</dt>
              <dd>
                <PaymentStatusBadge status={payment.payment_status} />
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="font-medium text-foreground/80">Amount</dt>
              <dd>{formatPaise(payment.payment_amount_paise)}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="font-medium text-foreground/80">Paid on</dt>
              <dd>{formatISTDateTime(payment.payment_captured_at)}</dd>
            </div>
          </dl>
        </section>
      </div>
    </AccountShell>
  );
}